import {
  createNewCheckPoint,
  getAllCheckPoints,
  getTotalActiveCheckPoints,
  getCheckPointById,
  listAllCheckPoints,
  updateCheckPoint,
  updateManyCheckPoints,
  getAllCheckPointsByGroupId,
  uploadCheckPointVideo,
  getMatchingCheckPointError,
  duplicateCheckPoint,
} from '@/api/check-point/check-point';
import {
  CheckPointLocalStorage,
  CheckpointTypeFilterType,
} from '@/entities/checkpoint';
import { TCheckPointSchema } from '@/schemas/check-point/check-point.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';

export const useCreateCheckPoint = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TCheckPointSchema) => createNewCheckPoint(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['getAllCheckPoints'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveCheckPoints'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingCheckPointError'],
      });
      queryClient.invalidateQueries({ queryKey: ['listAllCheckPoints'] });

      localStorage.setItem(CheckPointLocalStorage.IS_FROM_MUTATION, 'true');

      notify(t('checkPointCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useDuplicateCheckPoint = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, languageCode }: { id: string; languageCode: string }) =>
      duplicateCheckPoint(id, languageCode),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['getAllCheckPoints'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveCheckPoints'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingCheckPointError'],
      });
      queryClient.invalidateQueries({ queryKey: ['listAllCheckPoints'] });

      localStorage.setItem(CheckPointLocalStorage.IS_FROM_MUTATION, 'true');

      notify(t('checkPointDuplicatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetMatchingCheckPointError = (query?: TCheckPointSchema) => {
  return useQuery({
    queryKey: ['getMatchingCheckPointError', query],
    queryFn: () => getMatchingCheckPointError(query),
  });
};

export const useListAllCheckPoints = () => {
  return useQuery({
    queryKey: ['listAllCheckPoints'],
    queryFn: () => listAllCheckPoints(),
  });
};

export const useGetAllCheckPoints = (
  filter: CheckpointTypeFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: ['getAllCheckPoints', filter, page, limit, sortBy, sortDirection],
    queryFn: () =>
      getAllCheckPoints(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveCheckPoints = () => {
  return useQuery({
    queryKey: ['getTotalActiveCheckPoints'],
    queryFn: () => getTotalActiveCheckPoints(),
  });
};

export const useGetCheckPointById = (id: string) => {
  return useQuery({
    queryKey: ['getCheckPointById', id],
    queryFn: () => getCheckPointById(id),
    enabled: !!id,
  });
};

export const useUploadCheckPointVideo = ({
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (formData: FormData) => uploadCheckPointVideo(formData),
    onSuccess: (data) => {
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      console.error(error.error);
    },
  });
};

export const useUpdateCheckPoint = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (checkPoint: TCheckPointSchema) => updateCheckPoint(checkPoint),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllCheckPoints'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveCheckPoints'],
      });
      queryClient.invalidateQueries({ queryKey: ['getCheckPointById', id] });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingCheckPointError'],
      });
      queryClient.invalidateQueries({ queryKey: ['listAllCheckPoints'] });

      localStorage.setItem(CheckPointLocalStorage.IS_FROM_MUTATION, 'true');

      notify(t('checkPointUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(variables);
    },
    onError: (err: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      handleError?.(err);
      console.error('Error when updating check point:', err.error);
    },
  });
};

export const useUpdateManyCheckPoints = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (checkPoints: TCheckPointSchema[]) =>
      updateManyCheckPoints(checkPoints),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getCheckPointById', item?.id],
        });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllCheckPoints'] });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveCheckPoints'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMatchingCheckPointError'],
      });
      queryClient.invalidateQueries({ queryKey: ['listAllCheckPoints'] });

      localStorage.setItem(CheckPointLocalStorage.IS_FROM_MUTATION, 'true');

      notify(
        variables.length === 0
          ? t('common:theseElementsLinkedToEntitiesNotDeactivable')
          : variables.length > 1
            ? t('checkPointsUpdatedSuccessfully')
            : t('checkPointUpdatedSuccessfully'),
        NOTIFY.SUCCESS,
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating check points:', error);
    },
  });
};

export const useGetCheckPointsBygroupId = (groupId: string) => {
  return useQuery({
    queryKey: ['checkPointsBygroupId', groupId],
    queryFn: () => getAllCheckPointsByGroupId(groupId),
  });
};
